(ns ui.editors.nev-view-editor.sub
  (:require
   [reagent.ratom :as ratom]
   [re-frame.core :as rf]
   [ui.state.session.sub :as session-sub]
   [ui.editors.nev-view-editor.model :as model]
   [common.entity :as entity]
   [common.util :as cu]
   [common.schema.entities.view :as view-schema]
   [common.schema.entities.card :as card-schema]
   [frontend.state.current-session :as session]
   [common.localization :as loc]
   [oops.core :as oops]
   [clojure.string :as string]
   [common.schema.view.single-entity :as sevs]
   [reagent.core :as r]))

(defn temp-entity?
  [entity]
  (-> entity
      :db/id
      string?))

(defn changes-to-view?
  [original-view current-view]
  (not= (view-schema/select-update-fields original-view)
        (view-schema/select-update-fields current-view)))

(defn changes-to-card?
  [original-card current-card]
  (not= (card-schema/select-update-fields original-card)
        (card-schema/select-update-fields current-card)))

(rf/reg-sub
 ::state
 (fn [db _]
   (get-in db model/root-path)))

(rf/reg-sub
 ::visible?
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/visible-path)))

(rf/reg-sub
 ::submitting?
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/submitting-path)))

(rf/reg-sub
 ::location
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/location-path)))

(rf/reg-sub
 ::view-editor-tab
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/view-editor-tab-path)))

(rf/reg-sub
 ::selected-top-level-section
 :<- [::state]
 (fn [editor-state]
   (get editor-state model/selected-top-level-section-id-key)))

(rf/reg-sub
 ::selected-section
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/selected-section-path)))

(rf/reg-sub
 ::sidebar-tab
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/sidebar-tab-path)))

(rf/reg-sub
 ::create-new-view?
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/create-new-view-path)))

(rf/reg-sub
 ::clone-view?
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/clone-view-path)))

(rf/reg-sub
 ::update-view?
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/update-view-path)))

(rf/reg-sub
 ::view-id
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/view-id-path)))

(rf/reg-sub
 ::entities
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/entities-path)))

(rf/reg-sub
 ::selected-top-level-section-entity-type
 :<- [::state]
 (fn [editor-state]
   (get editor-state model/selected-top-level-section-entity-type-key)))

(rf/reg-sub
 ::entity-ids
 :<- [::entities]
 (fn [entities]
   (vec (keys entities))))

(rf/reg-sub
 ::view-entity
 :<- [::view-id]
 :<- [::entities]
 (fn [[view-id entities]]
   (let [view-entity (get entities view-id)]
     view-entity)))

(rf/reg-sub
 ::view-configuration
 :<- [::view-entity]
 (fn [view-entity]
   (:view/configuration view-entity)))

(rf/reg-sub
 ::all-view-cards
 :<- [::view-entity]
 :<- [::entities]
 (fn [[view-entity entities-by-id]]
   (->> view-entity
        :view/cards
        (mapv (fn [card-id]
                (get entities-by-id card-id))))))

(rf/reg-sub
 ::summary-section-cards
 :<- [::view-entity]
 :<- [::entities]
 (fn [[view-entity entities-by-id]]
   (let [entity-type (:view/entity-type view-entity)
         summary-section-card-ids (get-in view-entity [:view/configuration
                                                       :top-level-sections
                                                       :primary-entity
                                                       :sections
                                                       (sevs/summary-section-id entity-type)
                                                       :cards])
         summary-section-cards (->> summary-section-card-ids
                                    (mapv #(get entities-by-id %)))]
     summary-section-cards)))

(rf/reg-sub
 ::summary-section-cards-count
 :<- [::summary-section-cards]
 (fn [cards]
   (count cards)))

(rf/reg-sub
 ::original-entities
 :<- [::entity-ids]
 :<- [::session-sub/entities]
 (fn [[editor-entity-ids global-entities-by-id]]
   (->> editor-entity-ids
        (mapv (fn [eid]
                [eid (get global-entities-by-id eid)]))
        (into {}))))

(rf/reg-sub
 ::original-view-entity
 :<- [::view-id]
 :<- [::original-entities]
 (fn [[view-id original-entities-by-id]]
   (get original-entities-by-id view-id)))

(rf/reg-sub
 ::changes-to-view?
 :<- [::view-entity]
 :<- [::original-view-entity]
 (fn [[editor-view-entity original-view-entity]]
   (changes-to-view? original-view-entity editor-view-entity)))

(rf/reg-sub
 ::changes-to-cards?
 :<- [::all-view-cards]
 :<- [::original-entities]
 (fn [[cards original-entities-by-id]]
   (->> cards
        (some (fn [card]
                (let [original-card (get original-entities-by-id (:db/id card))]
                  (or (temp-entity? card)
                      (changes-to-card? original-card card))))))))

(rf/reg-sub
 ::changes?
 :<- [::changes-to-view?]
 :<- [::changes-to-cards?]
 (fn [[changes-to-view? changes-to-cards?]]
   (or changes-to-view? changes-to-cards?)))

;; FIXME - this may be sketchy
(rf/reg-sub-raw
 ::unsaved-changes-confirm
 (fn [_db-atom _]
   (let [unsaved-changes-atom (atom false)
         before-unload (fn [e]
                         (when @unsaved-changes-atom
                           ;; TODO: the message is not apparently visible anywher
                           ;; But it just needs to exist for this to work
                           ;; Investigate if this will show to user in some browsers
                           (let [msg "You have unsaved changes"]
                             (oops/oset! e "returnValue" msg)
                             msg)))]
     (oops/ocall js/window "addEventListener" "beforeunload" before-unload)
     (ratom/make-reaction
      (fn []
        (let [changes? @(rf/subscribe [::changes?])]
          (reset! unsaved-changes-atom changes?)
          nil))
      :on-dispose (fn []
                    (oops/ocall js/window "removeEventListener" "beforeunload" before-unload))))))

(rf/reg-sub
 ::view-access-level
 :<- [::view-entity]
 (fn [view-entity]
   (:view/access-level view-entity)))

(rf/reg-sub
 ::view-entity-type
 :<- [::view-entity]
 (fn [view-entity]
   (:view/entity-type view-entity)))

(rf/reg-sub
 ::primary-entity-type
 :<- [::view-entity]
 (fn [view-entity]
   (:view/entity-type view-entity)))

(rf/reg-sub
 ::available-linked-entity-sections
 :<- [::primary-entity-type]
 :<- [::view-configuration]
 :<- [::session-sub/session-context]
 (fn [[primary-entity-type
       view-configuration
       session-context]]
   (->> (sevs/available-sections session-context primary-entity-type)
        (filterv (fn [{:keys [id] :as _section}]
                   (sevs/linked-entity-section? id)))
        (mapv (fn [section]
                (assoc section :top-level-section? true)))
        (sort-by (fn [{:keys [id] :as _section}]
                   (get-in view-configuration [:top-level-sections id :ord]))))))

(rf/reg-sub
 ::hidden-top-level-sections
 :<- [::view-configuration]
 (fn [view-configuration]
   (->> view-configuration
        :top-level-sections
        (filterv (fn [[_section-id section-config]]
                   (:hidden? section-config)))
        (mapv first)
        set)))

(rf/reg-sub
 ::hidden-sections
 :<- [::selected-top-level-section]
 :<- [::view-configuration]
 (fn [[selected-top-level-section view-configuration]]
   (->> (get-in view-configuration [:top-level-sections selected-top-level-section :sections])
        (filterv (fn [[_section-id section-config]]
                   (:hidden? section-config)))
        (mapv first)
        set)))

(rf/reg-sub
 ::sections
 :<- [::primary-entity-type]
 :<- [::selected-top-level-section]
 :<- [::session-sub/session-context]
 (fn [[primary-entity-type
       selected-top-level-section
       session-context]]
   (if (= selected-top-level-section :primary-entity)
     (->> (sevs/available-sections session-context primary-entity-type)
          (filterv (fn [{:keys [id] :as _section}]
                     (sevs/primary-entity-section? id))))
     (let [section (get sevs/sections-by-id selected-top-level-section)
           entity-type (-> section
                           :entity-types
                           first)]
       (->> (sevs/available-sections session-context entity-type)
            (filterv (fn [{:keys [id] :as _section}]
                       (sevs/primary-entity-section? id))))))))

(rf/reg-sub
 ::current-account-is-view-owner?
 :<- [::view-entity]
 :<- [::session-sub/account]
 (fn [[view-entity current-account]]
   (view-schema/view-owner? current-account view-entity)))

(rf/reg-sub
 ::card-write-access?
 :<- [::entities]
 :<- [::session-sub/account]
 :<- [::session-sub/current-permissions]
 (fn [[editor-entities-by-id account account-current-permissions]
      [_ card-id]]
   (let [original-card (get editor-entities-by-id card-id)]
     (card-schema/has-write-access? account account-current-permissions original-card))))

(rf/reg-sub
 ::view-access-teams-and-persons
 :<- [::view-entity]
 :<- [::session-sub/entities]
 (fn [[view-entity global-entities-by-id]]
   (let [owner-id (:common/owner view-entity)
         owner-account (get global-entities-by-id owner-id)
         read-access (:view/read-access view-entity)
         write-access (:view/write-access view-entity)
         share-access (:view/share-access view-entity)
         selected-options (concat
                           (->> read-access
                                (map (fn [eid]
                                       {:value eid :read-access? true})))
                           (->> write-access
                                (map (fn [eid]
                                       {:value eid :write-access? true})))
                           (->> share-access
                                (map (fn [eid]
                                       {:value eid :share-access? true}))))
         selected-options (->> selected-options
                               (mapv (fn [{:keys [value] :as opt}]
                                       (let [entity (get global-entities-by-id value)
                                             entity-type (:entity/type entity)
                                             label (entity/display-string entity)]
                                         (assoc opt
                                                :label label
                                                :entity-type entity-type))))
                               (sort-by :label))
         selected-options (vec
                           (concat [{:value owner-id
                                     :label (entity/display-string owner-account)
                                     :entity-type :entity.type/account
                                     :owner? true}]
                                   selected-options))]
     selected-options)))

(rf/reg-sub-raw
 ::view-access-teams-and-persons-options
 (fn [_db-atom _]
   (ratom/reaction
    (let [current-account-id @session/account-id
          accounts @(r/track session/tenant-users)
          teams @session/teams
          selected-options @(rf/subscribe [::view-access-teams-and-persons])
          selected-option-ids-set (->> selected-options
                                       (map :value)
                                       (set))
          available-options
          (->> (concat teams accounts)
               (remove (fn [team-or-person]
                         (let [candidate-id (:db/id team-or-person)]
                           (or (contains? selected-option-ids-set candidate-id)
                               (= candidate-id current-account-id)))))
               (mapv (fn [team-or-person]
                       {:value (:db/id team-or-person)
                        :label (entity/display-string team-or-person)})))]
      available-options))))

(rf/reg-sub
 ::view-name
 :<- [::view-entity]
 (fn [view-entity]
   (:view/name view-entity)))

(rf/reg-sub
 ::view-description
 :<- [::view-entity]
 (fn [view-entity]
   (:view/description view-entity)))

;; This does not work do not use - just for testing and reference
;; Something like this may work if we want to prevent user from going back when
;; they have unsaved changes
(rf/reg-sub-raw
 ::change-listener
 (fn [db-atom]
   (let [pushed-state-atom (atom false)
         unsaved-changes-atom (atom false)
         leave-confirmed-atom (atom false)
         popstate-listener (fn [e]
                             (let [unsaved-changes? @unsaved-changes-atom
                                   state (oops/oget e "state")
                                   push (when state
                                          (oops/oget state "push"))
                                   leave-confirmed? @leave-confirmed-atom]
                               ;(js/console.log "POP " e)
                               (when (and (not push)
                                          (not leave-confirmed?)
                                          unsaved-changes?)
                                 (when (js/window.confirm "Unsaved changes, are you sure you want to go back")
                                   (reset! leave-confirmed-atom true)
                                   (js/window.history.back)))))
         unsaved-changes-hash "#unsaved-changes"
         get-hash (fn []
                    (let [location (oops/oget js/window "location")
                          hash (oops/oget location "hash")]
                      hash))

         remove-unsaved-changes-hash (fn []
                                       (let [pushed? @pushed-state-atom
                                             location (oops/oget js/window "location")
                                             href (oops/oget location "href")
                                             [url-wo-hash] (string/split href #"#")]
                                         (if pushed?
                                           (js/window.history.back)
                                           (js/window.history.replaceState (clj->js {:replace "yes"}) (pr-str url-wo-hash) (str url-wo-hash)))))]
     ;(js/console.log "add popstate listener")
     (oops/ocall js/window "addEventListener" "popstate" popstate-listener)
     (ratom/make-reaction
      (fn []
        (let [changes-to-view? @(rf/subscribe [::changes-to-view?])
              changes-to-cards? @(rf/subscribe [::changes-to-cards?])
              changes? (or changes-to-view? changes-to-cards?)
              pushed-state? @pushed-state-atom]
          (reset! unsaved-changes-atom changes?)
          (if changes?
            (when-not (= (get-hash) unsaved-changes-hash)
              ;(js/console.log "unsaved changes, push")
              (js/window.history.pushState (clj->js {:push "yes"}) (pr-str unsaved-changes-hash) (str unsaved-changes-hash))
              (reset! pushed-state-atom true))

            (when (= (get-hash) unsaved-changes-hash)
              (remove-unsaved-changes-hash))))

        true)
      {:on-dispose (fn []
                     ;(js/console.log "remove popstate listener")
                     (oops/ocall js/window "removeEventListener" "popstate" popstate-listener)
                     (when (= (get-hash) unsaved-changes-hash)
                       (remove-unsaved-changes-hash)))}))))

(rf/reg-sub
 ::submit-button-state
 :<- [::submitting?]
 :<- [::create-new-view?]
 :<- [::clone-view?]
 :<- [::update-view?]
 :<- [::view-name]
 :<- [::all-view-cards]
 :<- [::changes-to-view?]
 :<- [::changes-to-cards?]
 :<- [::summary-section-cards-count]
 (fn [[submitting? create-new-view? clone-view?
       update-view? view-name cards
       changes-to-view? changes-to-cards?
       summary-section-cards-count]]
   (let [label (cond
                 create-new-view?
                 (loc/tr :view-editor/create-view)
                 clone-view?
                 (loc/tr :view-editor/clone-view)
                 update-view?
                 (loc/tr :view-editor/update-view))
         action-type (cond
                       create-new-view? :create
                       clone-view? :clone
                       update-view? :update)
         no-name? (cu/empty-value? view-name)
         no-cards? (cu/empty-value? cards)
         no-summary-section-cards? (zero? summary-section-cards-count)
         changes? (or changes-to-view? changes-to-cards?)
         disabled? (or submitting?
                       (not changes?)
                       no-name?
                       no-cards?
                       no-summary-section-cards?)
         tooltip-label (cond
                         (and (not create-new-view?)
                              (not clone-view?)
                              (not changes?))
                         (loc/tr :no-changes)
                         (and no-name? no-cards?)
                         (loc/tr :view-editor/add-view-name-and-at-least-one-card)
                         no-summary-section-cards?
                         (loc/tr :view-editor/add-at-least-one-card-to-summary-section)
                         no-name?
                         (loc/tr :view-editor/add-view-name)
                         no-cards?
                         (loc/tr :view-editor/add-at-least-one-card)

                         :else nil)]
     {:label label
      :disabled? disabled?
      :action-type action-type
      :tooltip-label tooltip-label})))

(rf/reg-sub
 ::add-card-wizard-step
 :<- [::state]
 (fn [editor-state]
   (:add-card-wizard-step editor-state)))

(rf/reg-sub
 ::add-card-wizard-card-type
 :<- [::state]
 (fn [editor-state]
   (:add-card-wizard-card-type editor-state)))

(rf/reg-sub
 ::card-type-options
 :<- [::selected-top-level-section-entity-type]
 (fn [entity-type]
   (->> card-schema/card-type-options
        (map
         (fn [{:keys [value]}]
           (let [description (card-schema/get-card-type-description
                              {:card-type value
                               :entity-type entity-type
                               :tr loc/tr})]
             {:value value
              :label (loc/tr value)
              :description description}))))))

(rf/reg-sub
 ::card-template-options
 :<- [::add-card-wizard-card-type]
 :<- [::selected-top-level-section-entity-type]
 :<- [::session-sub/entities-for-type :entity.type/card]
 :<- [::session-sub/account]
 :<- [::session-sub/current-permissions]
 (fn [[card-type entity-type all-cards account current-permissions]]
   (->> all-cards
        (filterv (fn [card]
                   (and (= (:card/type card) card-type)
                        (= (:card/entity-type card) entity-type)
                        (card-schema/has-read-access? account current-permissions card))))
        (sort-by :card/title)
        (mapv (fn [card]
                {:card card})))))

(rf/reg-sub
 ::preview-entity-id
 :<- [::state]
 (fn [editor-state]
   (get-in editor-state model/preview-entity-id-path)))

(rf/reg-sub
 ::preview-device
 :<- [::state]
 (fn [editor-state]
   (get editor-state model/preview-device-key)))

;; Todo this should be replaced by opensearch
(rf/reg-sub
 ::preview-entity-options
 :<- [::view-entity-type]
 :<- [::session-sub/entity-types]
 (fn [[entity-type entities-by-type]]
   (let [entities (get entities-by-type entity-type)
         sorted-options (->> entities
                             (map (fn [entity]
                                    {:value (:db/id entity)
                                     :label (entity/display-string entity)}))
                             (sort-by :label))]
     sorted-options)))

(rf/reg-sub
 ::view-sections
 :<- [::view-entity-type]
 :<- [::session-sub/session-context]
 (fn [[entity-type session-ctx]]
   (sevs/available-sections session-ctx entity-type)))

(rf/reg-sub
 ::selected-section-title
 :<- [::selected-section]
 :<- [::view-sections]
 (fn [[selected-section sections]]
   (->> selected-section
        (get sevs/sections-by-id)
        :title)))

(rf/reg-sub
 ::selected-section-cards
 :<- [::selected-top-level-section]
 :<- [::selected-section]
 :<- [::view-configuration]
 :<- [::entities]
 (fn [[selected-top-level-section
       selected-section
       view-configuration
       entities]]
   (let [path [:top-level-sections selected-top-level-section :sections selected-section :cards]
         cards (->> (get-in view-configuration path)
                    (mapv #(get entities %)))]
     cards)))

(rf/reg-sub
 ::can-add-and-remove-cards-from-selected-section?
 :<- [::view-entity-type]
 :<- [::selected-section]
 (fn [[view-entity-type selected-section]]
   (->> selected-section
        (get sevs/sections-by-id)
        :can-add-and-remove-cards?)))

(rf/reg-sub
 ::selected-section-not-available-in-nev-2024?
 :<- [::selected-section]
 (fn [selected-section-id]
   (-> selected-section-id
       sevs/available-in-nev-2024?
       not)))

(rf/reg-sub
 ::no-module-access-to-selected-section?
 :<- [::selected-section]
 :<- [::session-sub/session-context]
 (fn [[selected-section-id session-ctx]]
   (->> selected-section-id
        (get sevs/sections-by-id)
        (sevs/section-access session-ctx)
        (= :no-module-access))))
